import { IonCardHeader, IonCardTitle, IonCol, IonGrid, IonIcon, IonLabel, IonRippleEffect, IonRow, IonSegment, IonSegmentButton, IonText, useIonAlert } from '@ionic/react';
import { unwrapResult } from '@reduxjs/toolkit';
import { pricetagsOutline, timeOutline } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { logTransaction } from '../../common/log';

import { getHoursMinutes, getStateDateAsString, getStateStringAsDate } from '../../common/util';
import { ClockingDirection } from '../../models/dms/clocking.model';
import { UNKNOWN_TECH } from '../../models/dms/technician.model';
import { IThunkError } from '../../models/error.model';
import { LogType } from '../../models/log.model';
import { useClockOffAttendanceModal } from '../../providers/ClockOffAttendanceModalProvider';
import { useDataSync } from '../../providers/DataSyncProvider';
import { useLocation } from '../../providers/LocationProvider';
import { useToast } from '../../providers/ToastProvider';
import store from '../../store';
import { setIsInActionState } from '../../store/appSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { breakAttendanceClockingAsync, clockOnAttendanceClockingAsync, resumeAttendanceClockingAsync } from '../../store/technicianClockingSlice';
import AttendanceTimer from '../attendanceTimer/AttendanceTimer';

import { IonCardWrapper } from './AttendanceClockingCard.styles';

interface IComponentProps {
  simpleType: boolean;
}

const AttendanceClockingCard: React.FC<IComponentProps> = ({ simpleType }) => {
  const { technician } = useAppSelector(state => state.auth);
  const { attendanceClocking } = useAppSelector(state => state.technicianClocking);
  const [attendanceDirection, setAttendanceDirection] = useState(attendanceClocking?.direction || ClockingDirection.Off);
  const { state: { requestQueueSyncTimer } } = useDataSync();
  const { actions: { errorToast } } = useToast();
  const { actions: { showClockOffAttendanceModal } } = useClockOffAttendanceModal();
  const { actions: { pauseDistanceTracking, resumeDistanceTracking } } = useLocation();

  const dispatch = useAppDispatch();
  const [presentAlert] = useIonAlert();

  useEffect(() => {
    setAttendanceDirection(attendanceClocking?.direction || ClockingDirection.Off);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attendanceClocking?.direction]);

  const handleClockInButtonClick = async () => {
    if (attendanceDirection !== ClockingDirection.On) {
      await presentAlert({
        header: 'Clock In?',
        subHeader: '',
        message: 'Are you sure you want to clock in attendance?',
        buttons: [
          {
            text: 'No',
            role: 'cancel',
            cssClass: 'secondary-button',
            handler: () => {
              const { attendanceClocking } = store.getState().technicianClocking;
              setAttendanceDirection(attendanceClocking?.direction || ClockingDirection.Off);
            }
          }, {
            text: 'Yes',
            cssClass: 'secondary-button',
            handler: () => {
              clockOnAttendance();
            }
          }
        ],
        onDidDismiss: () => {
          dispatch(setIsInActionState(false));
        }
      });

      dispatch(setIsInActionState(true));
    }

    setAttendanceDirection(ClockingDirection.On);
  };

  const handleClockOutButtonClick = () => {
    if (attendanceClocking?.direction !== ClockingDirection.Off) {
      showClockOffAttendanceModal(handleClockOffAttendanceModalDismiss);
    }

    setAttendanceDirection(ClockingDirection.Off);
  };

  const handleClockBreakButtonClick = async () => {
    if (attendanceClocking?.direction === ClockingDirection.On) {
      await presentAlert({
        header: 'Break?',
        subHeader: '',
        message: 'Are you sure you want to go on a break?',
        buttons: [
          {
            text: 'No',
            role: 'cancel',
            cssClass: 'secondary-button',
            handler: () => {
              const { attendanceClocking } = store.getState().technicianClocking;
              setAttendanceDirection(attendanceClocking?.direction || ClockingDirection.Off);
            }
          }, {
            text: 'Yes',
            cssClass: 'secondary-button',
            handler: () => {
              breakAttendance();
            }
          }
        ],
        onDidDismiss: () => {
          dispatch(setIsInActionState(false));
        }
      });

      dispatch(setIsInActionState(true));
    }

    setAttendanceDirection(ClockingDirection.Break);
  };

  const clockOnAttendance = async () => {
    const startDateTime = new Date();
    const { lastPosition } = store.getState().location;
    const { distanceTrackingUserActivated } = store.getState().settings;

    // If clock on from break, automatically clock on labour as well
    if (attendanceClocking?.direction === ClockingDirection.Break) {
      try {
        logTransaction(LogType.Info, `Resume attendance`);

        // Resume tracking location
        if (distanceTrackingUserActivated) {
          await resumeDistanceTracking();
        }

        const resultAction = await dispatch(resumeAttendanceClockingAsync({
          startDateTime: getStateDateAsString(startDateTime),
          position: lastPosition,
          resumeLabour: true,
        }));
        unwrapResult(resultAction);
        requestQueueSyncTimer?.fire();
      } catch (ex) {
        const err = (ex as IThunkError).message;
        logTransaction(LogType.Error, `Failed to resume attendance. ${err}`);
        errorToast(err);
      }
    } else {
      try {
        logTransaction(LogType.Info, `Clock in attendance requested`);
        const resultAction = await dispatch(clockOnAttendanceClockingAsync({
          techCode: technician?.code || UNKNOWN_TECH,
          startDateTime: getStateDateAsString(startDateTime),
          position: lastPosition
        }));
        unwrapResult(resultAction);
        requestQueueSyncTimer?.fire();
      } catch (ex) {
        const err = (ex as IThunkError).message;
        logTransaction(LogType.Error, `Failed to clock in attendance. ${err}`);
        errorToast(err);
      }
    }
  };

  const breakAttendance = async () => {
    const startDateTime = new Date();
    const { lastPosition } = store.getState().location;
    const { distanceTrackingUserActivated } = store.getState().settings;

    try {
      logTransaction(LogType.Info, `Break attendance requested`);

      // Pause tracking location
      if (distanceTrackingUserActivated) {
        await pauseDistanceTracking();
      }

      const resultAction = await dispatch(breakAttendanceClockingAsync({
        techCode: technician?.code || UNKNOWN_TECH,
        endDateTime: getStateDateAsString(startDateTime),
        position: lastPosition
      }));
      unwrapResult(resultAction);

      requestQueueSyncTimer?.fire();
    } catch (ex) {
      const err = (ex as IThunkError).message;
      logTransaction(LogType.Error, `Failed to break attendance. ${err}`);
      errorToast(err);
    }
  };

  const handleClockOffAttendanceModalDismiss = () => {
    const { attendanceClocking } = store.getState().technicianClocking;
    //setShowClockOffAttendanceModal(false);
    setAttendanceDirection(attendanceClocking?.direction || ClockingDirection.Off);
  };

  return (
    <IonCardWrapper className={`clocking-card ${simpleType ? 'simple' : ''}`}>
      <IonRippleEffect type="bounded" />
      {
        (() => {
          switch (attendanceClocking?.direction) {

            case ClockingDirection.On:
              // Clocked on
              return <IonCardHeader className={simpleType ? 'ion-no-padding' : ''}>
                <IonGrid className="ion-no-margin">
                  <IonRow>
                    <IonCol>
                      <div className="flexed">
                        <IonIcon icon={pricetagsOutline} size="large" className="title-icon" color="medium" />
                        {!simpleType && <div className="flexed-fill">
                          <div className="flexed col ion-next-nowrap">
                            <IonCardTitle className="flexed">Attendance</IonCardTitle>
                            <div className="ion-next-nowrap">
                              <IonIcon icon={timeOutline}></IonIcon> {getHoursMinutes(getStateStringAsDate(attendanceClocking.resumeDateTime || attendanceClocking.startDateTime))}
                            </div>
                          </div>
                        </div>}
                      </div>
                    </IonCol>
                    <IonCol className="ion-text-end">
                      <AttendanceTimer />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonCardHeader>;

            case ClockingDirection.Break:
              // Break
              return <IonCardHeader className={simpleType ? 'ion-no-padding' : ''}>
                <IonGrid className="ion-no-margin">
                  <IonRow>
                    <IonCol>
                      <div className="flexed">
                        <IonIcon icon={pricetagsOutline} size="large" className="title-icon" color="medium" />
                        {!simpleType && <div className="flexed-fill">
                          <div className="flexed col ion-next-nowrap">
                            <IonCardTitle className="flexed">Attendance</IonCardTitle>
                            <div className="ion-next-nowrap">
                              <IonIcon icon={timeOutline}></IonIcon> {getHoursMinutes(getStateStringAsDate(attendanceClocking.resumeDateTime || attendanceClocking.startDateTime))}
                            </div>
                          </div>
                        </div>}
                      </div>
                    </IonCol>
                    <IonCol className="ion-text-end">
                      <AttendanceTimer />
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonCardHeader>;

            default:
              // Clocked off
              return <IonCardHeader className={simpleType ? 'ion-no-padding' : ''}>
                <IonGrid className="ion-no-margin">
                  <IonRow>
                    <IonCol>
                      <div className="flexed">
                        <IonIcon icon={pricetagsOutline} size="large" className="title-icon" color="medium" />
                        {!simpleType && <div className="flexed-fill">
                          <div className="flexed col ion-next-nowrap">
                            <IonCardTitle className="flexed">Attendance</IonCardTitle>
                          </div>
                        </div>}
                      </div>
                    </IonCol>
                    <IonCol className="ion-text-end">
                      <IonText color="medium" className="ion-text-nowrap">
                        <h1 className="time-duration ion-no-margin">00:00</h1>
                        <h1 className="time-duration time-duration-seconds ion-no-margin">00</h1>
                      </IonText>
                    </IonCol>
                  </IonRow>
                </IonGrid>
              </IonCardHeader>;
          }
        })()
      }
      {!simpleType &&
        <IonSegment value={attendanceDirection.toString() || '0'} className="segment">
          <IonSegmentButton value={ClockingDirection.On.toString()} className="clock-in-button" onClick={() => handleClockInButtonClick()}>
            <IonLabel>In</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value={ClockingDirection.Break.toString()} className="break-button" disabled={attendanceDirection === ClockingDirection.Off} onClick={() => handleClockBreakButtonClick()}>
            <IonLabel>Break</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value={ClockingDirection.Off.toString()} className="clock-out-button" onClick={() => handleClockOutButtonClick()}>
            <IonLabel>Out</IonLabel>
          </IonSegmentButton>
        </IonSegment>
      }
    </IonCardWrapper>
  );
};

export default AttendanceClockingCard;
